/**
 * Minimax AI Strategy with Alpha-Beta Pruning
 *
 * Advanced AI implementation using minimax algorithm with:
 * - Alpha-beta pruning for performance optimization
 * - Iterative deepening for time management
 * - Move ordering for better pruning
 * - Transposition table for cached results
 * - Configurable search depth and time limits
 */

import { AIDifficulty, PlayerColor, BoardPosition } from '@/types/game-types';
import type { GameState } from '@/core/models/GameState';
import { AIStrategyBase, type AIMoveResult, AIUtils } from '../ai-strategy-base';
import { evaluateAllMoves } from '@/core/utils/move-evaluator';
import { AdvancedEvaluator, quickEvaluate } from './evaluation';
import { GameSimulator, type SimulatedGameState } from '../game-simulator';

/**
 * Minimax AI configuration
 */
export interface MinimaxConfig {
  maxDepth: number;
  maxTime: number;
  useIterativeDeepening: boolean;
  useTranspositionTable: boolean;
  useMoveOrdering: boolean;
  aspirationWindow?: number;
}

/**
 * Transposition table entry
 */
interface TranspositionEntry {
  depth: number;
  score: number;
  flag: 'exact' | 'lower' | 'upper';
  bestMove?: BoardPosition;
  timestamp: number;
}

/**
 * Search result from minimax
 */
interface SearchResult {
  score: number;
  bestMove: BoardPosition;
  depth: number;
  nodesEvaluated: number;
  timeElapsed: number;
  terminated: boolean;
}

/**
 * Minimax AI Strategy Implementation
 */
export class MinimaxAI extends AIStrategyBase {
  public readonly difficulty = AIDifficulty.MINIMAX;
  public readonly name = 'Minimax AI';
  public readonly description = 'Advanced AI using minimax with alpha-beta pruning';

  private _config: MinimaxConfig;
  private _transpositionTable = new Map<string, TranspositionEntry>();
  private _killerMoves: BoardPosition[][] = [];
  private _historyTable = new Map<string, number>();
  private _nodesEvaluated = 0;
  private _startTime = 0;

  constructor(config: Partial<MinimaxConfig> = {}) {
    super();
    this._config = {
      maxDepth: 6,
      maxTime: 3000,
      useIterativeDeepening: true,
      useTranspositionTable: true,
      useMoveOrdering: true,
      aspirationWindow: 50,
      ...config,
    };

    // Initialize killer moves table
    for (let depth = 0; depth <= this._config.maxDepth; depth++) {
      this._killerMoves[depth] = [];
    }
  }

  protected async selectMoveImpl(
    gameState: GameState,
    player: PlayerColor,
    timeLimit: number,
  ): Promise<AIMoveResult> {
    this._startTime = Date.now();
    this._nodesEvaluated = 0;
    const effectiveTimeLimit = Math.min(timeLimit, this._config.maxTime);

    const validMoves = Array.from(gameState.validMoves.keys());

    if (validMoves.length === 0) {
      throw new Error('No valid moves available');
    }

    if (validMoves.length === 1) {
      // Only one move available
      return {
        position: validMoves[0]!,
        confidence: 1.0,
        evaluation: 0,
        thinkingTime: Date.now() - this._startTime,
        depth: 0,
        nodesEvaluated: 1,
      };
    }

    let searchResult: SearchResult;

    if (this._config.useIterativeDeepening) {
      searchResult = await this.iterativeDeepening(gameState, player, effectiveTimeLimit);
    } else {
      searchResult = await this.search(gameState, player, this._config.maxDepth, effectiveTimeLimit);
    }

    const confidence = this.calculateConfidence(searchResult, validMoves.length);

    return {
      position: searchResult.bestMove,
      confidence,
      evaluation: searchResult.score,
      thinkingTime: searchResult.timeElapsed,
      depth: searchResult.depth,
      nodesEvaluated: searchResult.nodesEvaluated,
    };
  }

  /**
   * Iterative deepening search
   */
  private async iterativeDeepening(
    gameState: GameState,
    player: PlayerColor,
    timeLimit: number,
  ): Promise<SearchResult> {
    let bestResult: SearchResult = {
      score: -Infinity,
      bestMove: Array.from(gameState.validMoves.keys())[0]!,
      depth: 1,
      nodesEvaluated: 0,
      timeElapsed: 0,
      terminated: false,
    };

    let lastScore = 0;
    let aspirationAlpha = -Infinity;
    let aspirationBeta = Infinity;

    for (let depth = 1; depth <= this._config.maxDepth; depth++) {
      const depthStartTime = Date.now();

      // Check time limit
      if (Date.now() - this._startTime >= timeLimit * 0.9) {
        break;
      }

      // Set aspiration window if available and enabled
      if (this._config.aspirationWindow && depth > 2) {
        aspirationAlpha = lastScore - this._config.aspirationWindow;
        aspirationBeta = lastScore + this._config.aspirationWindow;
      }

      try {
        const result = await this.minimaxSearch(
          gameState,
          depth,
          aspirationAlpha,
          aspirationBeta,
          true,
          player,
          timeLimit,
        );

        // If search completed successfully, update best result
        if (result.score > -Infinity) {
          bestResult = {
            score: result.score,
            bestMove: result.bestMove!,
            depth,
            nodesEvaluated: this._nodesEvaluated,
            timeElapsed: Date.now() - this._startTime,
            terminated: false,
          };
          lastScore = result.score;
        }

        // Research with wider window if aspiration search failed
        if (this._config.aspirationWindow &&
            (result.score <= aspirationAlpha || result.score >= aspirationBeta)) {
          const widerResult = await this.minimaxSearch(
            gameState,
            depth,
            -Infinity,
            Infinity,
            true,
            player,
            timeLimit,
          );

          if (widerResult.score > -Infinity) {
            bestResult = {
              score: widerResult.score,
              bestMove: widerResult.bestMove!,
              depth,
              nodesEvaluated: this._nodesEvaluated,
              timeElapsed: Date.now() - this._startTime,
              terminated: false,
            };
          }
        }

      } catch (error) {
        // Time limit reached, return best result so far
        break;
      }

      // Early termination for quick wins/losses
      if (Math.abs(bestResult.score) > 1000) {
        break;
      }
    }

    return bestResult;
  }

  /**
   * Single depth search
   */
  private async search(
    gameState: GameState,
    player: PlayerColor,
    depth: number,
    timeLimit: number,
  ): Promise<SearchResult> {
    const result = await this.minimaxSearch(
      gameState,
      depth,
      -Infinity,
      Infinity,
      true,
      player,
      timeLimit,
    );

    return {
      score: result.score,
      bestMove: result.bestMove!,
      depth,
      nodesEvaluated: this._nodesEvaluated,
      timeElapsed: Date.now() - this._startTime,
      terminated: result.score === -Infinity,
    };
  }

  /**
   * Minimax search with alpha-beta pruning
   */
  private async minimaxSearch(
    gameState: GameState | SimulatedGameState,
    depth: number,
    alpha: number,
    beta: number,
    isMaximizing: boolean,
    originalPlayer: PlayerColor,
    timeLimit: number,
  ): Promise<{ score: number; bestMove?: BoardPosition }> {
    this._nodesEvaluated++;

    // Check time limit
    if (Date.now() - this._startTime >= timeLimit) {
      return { score: -Infinity };
    }

    // Check transposition table
    if (this._config.useTranspositionTable) {
      const ttEntry = this.lookupTransposition(gameState, depth);
      if (ttEntry) {
        if (ttEntry.flag === 'exact') {
          return { score: ttEntry.score, bestMove: ttEntry.bestMove };
        } else if (ttEntry.flag === 'lower' && ttEntry.score >= beta) {
          return { score: ttEntry.score, bestMove: ttEntry.bestMove };
        } else if (ttEntry.flag === 'upper' && ttEntry.score <= alpha) {
          return { score: ttEntry.score, bestMove: ttEntry.bestMove };
        }
      }
    }

    // Terminal node evaluation
    if (depth === 0 || gameState.validMoves.size === 0) {
      // Convert to simulated state if needed for evaluation
      const simState = 'board' in gameState && 'scores' in gameState && 'currentPlayer' in gameState
        ? gameState as SimulatedGameState
        : GameSimulator.fromGameState(gameState as GameState);

      // Create a GameState-like object for the evaluator
      const evalState = {
        board: simState.board,
        currentPlayer: simState.currentPlayer,
        validMoves: simState.validMoves,
        scores: simState.scores,
        gameOver: simState.gameOver,
      } as unknown as GameState;

      const evaluation = AdvancedEvaluator.evaluatePosition(evalState, originalPlayer);
      return { score: evaluation.totalScore };
    }

    // Get and order moves
    const moves = this.getOrderedMoves(gameState, depth, originalPlayer);
    let bestMove: BoardPosition | undefined;
    let bestScore = isMaximizing ? -Infinity : Infinity;

    for (const move of moves) {
      // Make the move
      const newGameState = this.makeMove(gameState, move);

      if (!newGameState) continue;

      // Recursive search
      const result = await this.minimaxSearch(
        newGameState,
        depth - 1,
        alpha,
        beta,
        !isMaximizing,
        originalPlayer,
        timeLimit,
      );

      // Update best score and move
      if (isMaximizing) {
        if (result.score > bestScore) {
          bestScore = result.score;
          bestMove = move;
        }
        alpha = Math.max(alpha, bestScore);
      } else {
        if (result.score < bestScore) {
          bestScore = result.score;
          bestMove = move;
        }
        beta = Math.min(beta, bestScore);
      }

      // Alpha-beta pruning
      if (beta <= alpha) {
        // Update killer moves
        this.updateKillerMoves(move, depth);
        break;
      }
    }

    // Store in transposition table
    if (this._config.useTranspositionTable) {
      this.storeTransposition(gameState, depth, bestScore, bestMove, alpha, beta);
    }

    return { score: bestScore, bestMove };
  }

  /**
   * Get ordered moves for better alpha-beta pruning
   */
  private getOrderedMoves(
    gameState: GameState | SimulatedGameState,
    depth: number,
    player: PlayerColor,
  ): BoardPosition[] {
    const validMoves = Array.from(gameState.validMoves.keys());

    if (!this._config.useMoveOrdering) {
      return validMoves;
    }

    // Evaluate moves for ordering
    const evaluatedMoves = validMoves.map(move => {
      const flippedPositions = gameState.validMoves.get(move) || [];
      const score = quickEvaluate(gameState.board as Uint8Array, move, player, [...flippedPositions]);

      // Bonus for killer moves
      let bonus = 0;
      if (this._killerMoves[depth] && this._killerMoves[depth].includes(move)) {
        bonus += 1000;
      }

      // History table bonus
      const historyKey = `${move}_${player}`;
      const historyScore = this._historyTable.get(historyKey) || 0;
      bonus += historyScore;

      return { move, score: score + bonus };
    });

    // Sort by score descending (best moves first)
    evaluatedMoves.sort((a, b) => b.score - a.score);

    return evaluatedMoves.map(item => item.move);
  }

  /**
   * Make a move and return new game state
   */
  private makeMove(gameState: GameState | SimulatedGameState, move: BoardPosition): SimulatedGameState | null {
    const simState = 'board' in gameState && 'validMoves' in gameState && 'currentPlayer' in gameState
      ? gameState as SimulatedGameState
      : GameSimulator.fromGameState(gameState as GameState);

    return GameSimulator.makeMove(simState, move);
  }

  /**
   * Update killer moves heuristic
   */
  private updateKillerMoves(move: BoardPosition, depth: number): void {
    if (!this._killerMoves[depth]) {
      this._killerMoves[depth] = [];
    }

    const killers = this._killerMoves[depth];
    if (!killers.includes(move)) {
      killers.unshift(move);
      // Keep only top 2 killer moves per depth
      if (killers.length > 2) {
        killers.pop();
      }
    }
  }

  /**
   * Lookup transposition table
   */
  private lookupTransposition(gameState: GameState | SimulatedGameState, depth: number): TranspositionEntry | null {
    const key = this.getTranspositionKey(gameState);
    const entry = this._transpositionTable.get(key);

    if (entry && entry.depth >= depth) {
      return entry;
    }

    return null;
  }

  /**
   * Store result in transposition table
   */
  private storeTransposition(
    gameState: GameState | SimulatedGameState,
    depth: number,
    score: number,
    bestMove: BoardPosition | undefined,
    alpha: number,
    beta: number,
  ): void {
    const key = this.getTranspositionKey(gameState);

    let flag: 'exact' | 'lower' | 'upper';
    if (score <= alpha) {
      flag = 'upper';
    } else if (score >= beta) {
      flag = 'lower';
    } else {
      flag = 'exact';
    }

    this._transpositionTable.set(key, {
      depth,
      score,
      flag,
      bestMove,
      timestamp: Date.now(),
    });

    // Limit table size
    if (this._transpositionTable.size > 100000) {
      this.cleanupTranspositionTable();
    }
  }

  /**
   * Generate transposition table key
   */
  private getTranspositionKey(gameState: GameState | SimulatedGameState): string {
    // Simple hash based on board state and current player
    return `${Array.from(gameState.board).join('')}_${gameState.currentPlayer}`;
  }

  /**
   * Cleanup old transposition table entries
   */
  private cleanupTranspositionTable(): void {
    const now = Date.now();
    const cutoff = now - 300000; // 5 minutes

    for (const [key, entry] of this._transpositionTable.entries()) {
      if (entry.timestamp < cutoff) {
        this._transpositionTable.delete(key);
      }
    }
  }

  /**
   * Calculate confidence based on search result
   */
  private calculateConfidence(result: SearchResult, moveCount: number): number {
    let confidence = 0.7; // Base confidence

    // Higher confidence for deeper searches
    confidence += Math.min(result.depth / 10, 0.2);

    // Lower confidence if search was terminated early
    if (result.terminated) {
      confidence -= 0.3;
    }

    // Higher confidence for clear wins/losses
    if (Math.abs(result.score) > 500) {
      confidence += 0.2;
    }

    // Lower confidence with many moves available
    if (moveCount > 8) {
      confidence -= 0.1;
    }

    return Math.max(0.1, Math.min(0.95, confidence));
  }

  /**
   * Get configuration
   */
  getConfig(): MinimaxConfig {
    return { ...this._config };
  }

  /**
   * Update configuration
   */
  setConfig(config: Partial<MinimaxConfig>): void {
    this._config = { ...this._config, ...config };
  }

  /**
   * Clear caches
   */
  clearCaches(): void {
    this._transpositionTable.clear();
    this._historyTable.clear();
    for (let depth = 0; depth <= this._config.maxDepth; depth++) {
      this._killerMoves[depth] = [];
    }
  }
}

/**
 * Factory functions for creating minimax AI variants
 */
export const createMinimaxAI = {
  /**
   * Basic minimax with moderate depth
   */
  basic(): MinimaxAI {
    return new MinimaxAI({
      maxDepth: 4,
      maxTime: 2000,
      useIterativeDeepening: true,
    });
  },

  /**
   * Intermediate minimax with more depth
   */
  intermediate(): MinimaxAI {
    return new MinimaxAI({
      maxDepth: 6,
      maxTime: 3000,
      useIterativeDeepening: true,
      useTranspositionTable: true,
    });
  },

  /**
   * Advanced minimax with full features
   */
  advanced(): MinimaxAI {
    return new MinimaxAI({
      maxDepth: 8,
      maxTime: 5000,
      useIterativeDeepening: true,
      useTranspositionTable: true,
      useMoveOrdering: true,
      aspirationWindow: 50,
    });
  },

  /**
   * Custom minimax configuration
   */
  custom(config: Partial<MinimaxConfig>): MinimaxAI {
    return new MinimaxAI(config);
  },
};